
import {
  threeMatrixWorldToBlenderArray,
  threeMatrixWorldToBlenderTransform,
  threeToBlenderPositionArray,
} from './lightmapBlenderCoordinates.js';

import type { LightmapSceneExportObjectBase } from './lightmapSceneExportTypes.js';
import type { Actor } from '../../actors/Actor.js';
import type { SceneComponent } from '../../components/SceneComponent.js';
import type * as THREE from 'three';

export type LightmapMeshGeometryExport = {
  vertices: number[];
  indices?: number[];
  uvs?: number[];
};

export function hasExportableGeometry(geometry: THREE.BufferGeometry): boolean {
  const positionAttr = geometry.attributes.position;
  return !!positionAttr && positionAttr.count > 0;
}

export function extractLightmapMeshGeometryBlender(geometry: THREE.BufferGeometry): LightmapMeshGeometryExport {
  const positionAttr = geometry.attributes.position;
  if (!positionAttr) {
    return { vertices: [] };
  }

  const vertices: number[] = [];
  for (let i = 0; i < positionAttr.count; i++) {
    const [bx, by, bz] = threeToBlenderPositionArray(
      positionAttr.getX(i),
      positionAttr.getY(i),
      positionAttr.getZ(i),
    );
    vertices.push(bx, by, bz);
  }

  const uvAttr = geometry.attributes.uv;
  const uvs: number[] = [];
  if (uvAttr) {
    for (let i = 0; i < uvAttr.count; i++) {
      uvs.push(uvAttr.getX(i), 1 - uvAttr.getY(i));
    }
  }

  const index = geometry.index;
  const indices: number[] = [];
  if (index) {
    for (let i = 0; i < index.count; i++) {
      indices.push(index.getX(i));
    }
  }

  return {
    vertices,
    ...(indices.length > 0 ? { indices } : {}),
    ...(uvs.length === positionAttr.count * 2 ? { uvs } : {}),
  };
}

export function objectBaseFields(
  actor: Actor,
  component: SceneComponent,
  meshIndex: number,
  lightmapTargetId: string,
): Pick<
  LightmapSceneExportObjectBase,
  'lightmapTargetId' | 'actorUuid' | 'componentUuid' | 'componentType' | 'meshIndex' | 'displayName'
> {
  return {
    lightmapTargetId,
    actorUuid: actor.uuid,
    componentUuid: component.uuid,
    componentType: component.constructor.name,
    meshIndex,
    displayName: `${actor.name || actor.uuid}:${component.constructor.name}:${meshIndex}`,
  };
}

export function worldTransformForComponent(component: SceneComponent): {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  matrixWorldBlender: number[];
} {
  component.updateWorldMatrix(true, false);
  return {
    ...threeMatrixWorldToBlenderTransform(component.matrixWorld),
    matrixWorldBlender: threeMatrixWorldToBlenderArray(component.matrixWorld),
  };
}
